import React, { useEffect, useRef } from "react";
import MicrophoneOutlineIcon from "@src/lib/icons/18px/microphone-outline";
import XIcon from "@src/lib/icons/12px/x";
import {
  Dictation,
  DictationStatus,
  useDictation,
  UseDictationOptions,
  useIsDictationSupported,
} from "@src/features/aiAssist/hooks/useDictation";
import { Button } from "@src/lib/ui/Button";
import Spinner from "@src/lib/ui/Spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import { Textarea, TextareaProps } from "@src/lib/ui/Textarea";
import { cn } from "@src/lib/utils/cn";

type DictationToggleProps = {
  dictation: Dictation;
  disabled?: boolean;
  className?: string;
  // Keep focus (and the caret) in the field being dictated into. Rich text
  // editors ignore external value changes while focused, so they opt out.
  keepFocus?: boolean;
};

// The field locked (e.g. generation started): write nothing more into it.
const useAbortWhenDisabled = (dictation: Dictation, disabled?: boolean) => {
  const { abort } = dictation;
  useEffect(() => {
    if (disabled) abort();
  }, [disabled, abort]);
};

// The mic button that starts dictation. Renders nothing in browsers that
// cannot record audio.
export const DictationToggle = React.forwardRef<
  HTMLButtonElement,
  DictationToggleProps
>(({ dictation, disabled, className, keepFocus = true }, ref) => {
  useAbortWhenDisabled(dictation, disabled);
  if (!dictation.isSupported) return null;
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={dictation.toggle}
          onMouseDown={keepFocus ? (e) => e.preventDefault() : undefined}
          disabled={disabled || dictation.status !== "idle"}
          aria-label="Dictate"
          data-testid="dictation-button"
          data-status={dictation.status}
          className={cn(
            "shrink-0 text-icon-secondary hover:text-icon-primary",
            className,
          )}
        >
          <MicrophoneOutlineIcon />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Dictate</TooltipContent>
    </Tooltip>
  );
});
DictationToggle.displayName = "DictationToggle";

const SAMPLE_MS = 50;
const BAR_WIDTH = 2;
const BAR_GAP = 2;
// Below this loudness a slot draws as a dot, like silence in the reference.
const SILENCE_LEVEL = 0.12;

// Loudness history scrolling in from the right: dots for silence, bars for
// speech. Samples while recording and freezes on the last frame afterwards.
export const DictationWaveform: React.FC<{
  getLevel: () => number;
  status: DictationStatus;
  className?: string;
}> = ({ getLevel, status, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const levelsRef = useRef<number[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const draw = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      // Rounded: canvas sizes are integers, so a fractional DPR would
      // otherwise reallocate the canvas every frame.
      const pixelWidth = Math.round(width * ratio);
      const pixelHeight = Math.round(height * ratio);
      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = getComputedStyle(canvas).color;

      const slots = Math.floor(width / (BAR_WIDTH + BAR_GAP));
      const levels = levelsRef.current.slice(-slots);
      const middle = height / 2;
      for (let slot = 0; slot < slots; slot++) {
        // Right-aligned: the newest sample is the rightmost slot.
        const level = levels[levels.length - slots + slot] ?? 0;
        const x = slot * (BAR_WIDTH + BAR_GAP);
        if (level < SILENCE_LEVEL) {
          context.globalAlpha = 0.45;
          context.fillRect(x, middle - 0.75, BAR_WIDTH, 1.5);
        } else {
          context.globalAlpha = 1;
          const barHeight = Math.max(4, level * height);
          const top = middle - barHeight / 2;
          // roundRect is missing before Safari 16.4 / Firefox 112.
          if (context.roundRect) {
            context.beginPath();
            context.roundRect(x, top, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
            context.fill();
          } else {
            context.fillRect(x, top, BAR_WIDTH, barHeight);
          }
        }
      }
    };

    if (status !== "recording") {
      draw();
      return;
    }
    let frame = 0;
    let lastSample = 0;
    const tick = (now: number) => {
      if (now - lastSample >= SAMPLE_MS) {
        lastSample = now;
        levelsRef.current.push(getLevel());
        if (levelsRef.current.length > 2000) levelsRef.current.splice(0, 1000);
      }
      draw();
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [getLevel, status]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      data-testid="dictation-waveform"
      className={cn(
        "h-6 min-w-0 flex-1 text-icon-secondary transition-opacity",
        status === "transcribing" && "opacity-40",
        className,
      )}
    />
  );
};

// Replaces the input while dictating: cancel, live waveform, stop.
export const DictationRecordingBar: React.FC<{
  dictation: Dictation;
  className?: string;
  // Where focus goes when the bar closes, if it was on the bar (or lost).
  restoreFocusTo?: () => HTMLElement | null | undefined;
}> = ({ dictation, className, restoreFocusTo }) => {
  const isTranscribing = dictation.status === "transcribing";
  const barRef = useRef<HTMLDivElement>(null);
  const stopRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(restoreFocusTo);
  restoreFocusRef.current = restoreFocusTo;

  // Escape cancels dictation, not the dialog or popover around it: Radix
  // listens on document in the capture phase, so this listens on window.
  const abortRef = useRef(dictation.abort);
  abortRef.current = dictation.abort;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // The field the bar sits in (or replaced) counts too: a mouse user's
      // focus stays in the textarea while dictating.
      const scope = barRef.current?.parentElement;
      if (
        event.key === "Escape" &&
        event.target instanceof Node &&
        scope?.contains(event.target)
      ) {
        event.stopPropagation();
        abortRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, []);

  // The mic (or field) this replaced took focus with it: keep keyboard users
  // on the bar, and hand focus back to the field when it closes.
  useEffect(() => {
    const bar = barRef.current;
    const lost = (element: Element | null) =>
      !element || element === document.body || !!bar?.contains(element);
    if (lost(document.activeElement)) {
      stopRef.current?.focus({ preventScroll: true });
    }
    return () => {
      if (!lost(document.activeElement)) return;
      const target = restoreFocusRef.current?.();
      target?.focus({ preventScroll: true });
      // A remounted field starts with the caret at 0; put it after the text
      // (and the transcript) so typing continues where dictation left off.
      if (
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLInputElement
      ) {
        target.setSelectionRange(target.value.length, target.value.length);
      }
    };
  }, []);

  return (
    <div
      ref={barRef}
      data-testid="dictation-recording-bar"
      data-status={dictation.status}
      className={cn("flex min-w-0 items-center gap-2", className)}
    >
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        onClick={dictation.abort}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Cancel dictation"
        data-testid="dictation-cancel"
        className="shrink-0 text-icon-secondary hover:text-icon-primary"
      >
        <XIcon className="size-3" />
      </Button>
      <DictationWaveform
        getLevel={dictation.getLevel}
        status={dictation.status}
      />
      <button
        ref={stopRef}
        type="button"
        onClick={dictation.stop}
        onMouseDown={(e) => e.preventDefault()}
        disabled={isTranscribing}
        aria-label={isTranscribing ? "Transcribing…" : "Stop and transcribe"}
        aria-busy={isTranscribing}
        data-testid="dictation-stop"
        className="flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-action-primary text-icon-primary transition-colors hover:bg-state-hover focus-visible:ring-3 focus-visible:ring-brand-border focus-visible:outline-none disabled:cursor-progress disabled:border-border-primary"
      >
        {isTranscribing ? (
          <Spinner size={16} label="" className="text-icon-secondary" />
        ) : (
          <span className="size-2.5 rounded-sm bg-icon-primary" />
        )}
      </button>
    </div>
  );
};

type WithDictationProps = UseDictationOptions & {
  disabled?: boolean;
  children: (dictation: Dictation | null) => React.ReactNode;
};

// Hands a dictation controller to its children, or null where the browser
// cannot record. One component either way, so the field below never remounts
// when support is detected after mount (that would drop its autofocus).
export const WithDictation: React.FC<WithDictationProps> = ({
  children,
  disabled,
  ...options
}) => {
  const dictation = useDictation(options);
  useAbortWhenDisabled(dictation, disabled);
  return <>{children(dictation.isSupported ? dictation : null)}</>;
};

type DictationFieldProps = UseDictationOptions & {
  disabled?: boolean;
  className?: string;
  // The field; it gets extra bottom padding while the bar covers its end.
  children: (state: { isDictating: boolean }) => React.ReactNode;
};

// A multi-line field with a mic in its bottom-right corner. While dictating,
// the recording bar covers the bottom of the field.
export const DictationField: React.FC<DictationFieldProps> = ({
  children,
  className,
  disabled,
  ...options
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <WithDictation {...options} disabled={disabled}>
      {(dictation) => {
        const isDictating = !!dictation && dictation.status !== "idle";
        return (
          <div ref={containerRef} className={cn("relative", className)}>
            {children({ isDictating })}
            {dictation &&
              (isDictating ? (
                <DictationRecordingBar
                  dictation={dictation}
                  restoreFocusTo={() =>
                    containerRef.current?.querySelector<HTMLElement>(
                      "textarea, input, [contenteditable=true]",
                    )
                  }
                  className="absolute inset-x-1.5 bottom-1.5 rounded-full bg-bg-primary px-0.5 py-0.5"
                />
              ) : (
                <DictationToggle
                  dictation={dictation}
                  disabled={disabled}
                  className="absolute right-1.5 bottom-1.5"
                />
              ))}
          </div>
        );
      }}
    </WithDictation>
  );
};

// label/description would wrap the textarea and misplace the absolutely
// positioned mic, so DictationTextarea takes neither.
type DictationTextareaProps = Omit<
  TextareaProps,
  "value" | "onChange" | "label" | "description" | "wrapperClassName"
> & {
  value: string;
  onValueChange: (value: string) => void;
  containerClassName?: string;
};

// Textarea with a mic pinned to its bottom-right corner.
export const DictationTextarea = React.forwardRef<
  HTMLTextAreaElement,
  DictationTextareaProps
>(
  (
    { value, onValueChange, containerClassName, className, disabled, ...props },
    ref,
  ) => {
    const isSupported = useIsDictationSupported();
    return (
      <DictationField
        value={value}
        onChange={onValueChange}
        disabled={disabled}
        className={containerClassName}
      >
        {({ isDictating }) => (
          <Textarea
            ref={ref}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={disabled}
            // Last, so a caller's padding shorthand cannot drop the gutters.
            className={cn(
              "resize-none",
              className,
              isSupported && "pr-10",
              isDictating && "pb-11",
            )}
            {...props}
          />
        )}
      </DictationField>
    );
  },
);
DictationTextarea.displayName = "DictationTextarea";
